import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, MapPin } from "./icons";
import { PORTFOLIO_ITEMS } from "../data/portfolio";
import SectionHeading from "./SectionHeading";
import Reveal from "./Reveal";
import PortfolioCard from "./PortfolioCard";
import { useLanguage } from "../context/LanguageContext";

const COUNT = PORTFOLIO_ITEMS.length;
const AUTOPLAY_INTERVAL_MS = 4500;
// Below this fraction of the track's width, a drag is treated as a tap/flick
// that didn't mean to change slides and snaps back to where it started.
const DRAG_COMMIT_THRESHOLD = 0.12;

// One clone of the last item is prepended and one clone of the first item is
// appended so the track can slide seamlessly past the real ends; the index
// snaps back to the matching real slide (without a transition) once the
// clone has fully scrolled into view.
const SLIDES = [PORTFOLIO_ITEMS[COUNT - 1], ...PORTFOLIO_ITEMS, PORTFOLIO_ITEMS[0]];

export default function PortfolioGrid({ className = "" }: { className?: string }) {
  const { t } = useLanguage();
  const [index, setIndex] = useState(1);
  const [animated, setAnimated] = useState(true);
  const autoplayTimer = useRef<number>();
  const autoplayPaused = useRef(false);
  const trackRef = useRef<HTMLDivElement>(null);

  // Drag/swipe state — kept in refs (not state) for the per-pixel pointermove
  // updates, with dragX mirrored into state only to drive the transform
  // re-render each frame.
  const dragState = useRef<{ pointerId: number; startX: number; width: number } | null>(null);
  const [dragX, setDragX] = useState(0);
  const isDragging = dragState.current !== null;

  // Wraps into the valid clone range [0, COUNT+1] on every step, instead of
  // incrementing without bound. Background tabs throttle/skip CSS
  // transitions, so the transitionend-driven snap-back below can go long
  // stretches without firing (e.g. a tab left open for 10+ minutes) while
  // this interval keeps ticking — without the wrap here, `index` would
  // drift past the end of `SLIDES` and `SLIDES[index]` below would read as
  // undefined, crashing the render as soon as the tab comes back.
  const wrapIndex = (i: number) => {
    if (i > COUNT + 1) return 1;
    if (i < 0) return COUNT;
    return i;
  };

  const restartAutoplay = () => {
    window.clearInterval(autoplayTimer.current);
    autoplayTimer.current = window.setInterval(() => {
      if (!autoplayPaused.current) setIndex((i) => wrapIndex(i + 1));
    }, AUTOPLAY_INTERVAL_MS);
  };

  useEffect(() => {
    restartAutoplay();
    return () => window.clearInterval(autoplayTimer.current);
  }, []);

  const goTo = (direction: 1 | -1) => {
    setAnimated(true);
    setIndex((i) => wrapIndex(i + direction));
    restartAutoplay();
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Only the primary button/touch/pen contact starts a drag.
    if (e.button !== undefined && e.button !== 0) return;
    const width = trackRef.current?.getBoundingClientRect().width ?? 0;
    if (width === 0) return;
    dragState.current = { pointerId: e.pointerId, startX: e.clientX, width };
    autoplayPaused.current = true;
    setAnimated(false);
    setDragX(0);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragState.current;
    if (!drag || e.pointerId !== drag.pointerId) return;
    setDragX(e.clientX - drag.startX);
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragState.current;
    if (!drag || e.pointerId !== drag.pointerId) return;
    const fraction = dragX / drag.width;
    dragState.current = null;
    setDragX(0);
    setAnimated(true);
    if (Math.abs(fraction) > DRAG_COMMIT_THRESHOLD) {
      setIndex((i) => wrapIndex(i + (fraction < 0 ? 1 : -1)));
    }
    autoplayPaused.current = false;
    restartAutoplay();
  };

  const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    // Guard against bubbled transitionend events from descendants (e.g. the
    // card image's hover-zoom transition) — only react to the track's own.
    if (e.target !== e.currentTarget) return;
    if (index === 0) {
      setAnimated(false);
      setIndex(COUNT);
    } else if (index === COUNT + 1) {
      setAnimated(false);
      setIndex(1);
    }
  };

  // Re-enable the transition on the next frame after a transition-less snap.
  useEffect(() => {
    if (animated) return;
    const id = requestAnimationFrame(() => setAnimated(true));
    return () => cancelAnimationFrame(id);
  }, [animated]);

  const activeItem = SLIDES[index] ?? SLIDES[1];

  return (
    <section id="portfolio" className={`bg-white py-24 md:py-32 ${className}`}>
      <div className="container-industrial">
        <Reveal>
          <SectionHeading
            title={t("portfolioHeading")}
            description={t("portfolioDescription")}
          />
        </Reveal>

        <div
          className="relative mt-10 overflow-hidden"
          onMouseEnter={() => {
            autoplayPaused.current = true;
          }}
          onMouseLeave={() => {
            autoplayPaused.current = false;
          }}
        >
          <div
            ref={trackRef}
            className={`flex touch-pan-y select-none ${
              isDragging ? "cursor-grabbing" : "cursor-grab"
            } ${animated ? "transition-transform duration-[600ms] ease-engineered" : ""}`}
            style={{
              transform: `translateX(calc(-${index * 100}% + ${dragX}px))`,
            }}
            onTransitionEnd={handleTransitionEnd}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          >
            {SLIDES.map((item, i) => (
              <div key={`${item.id}-${i}`} className="w-full shrink-0">
                <PortfolioCard item={item} />
              </div>
            ))}
          </div>
          {/* Fixed to the viewport (not each sliding card) so the text-legibility
              fade never resets or double-stacks as slides transition underneath it.
              Also has to live outside the track above: the track's `transform`
              creates its own stacking context, so nothing inside it — no matter
              the z-index — can ever paint above a sibling like this one. */}
          <div className="pointer-events-none absolute inset-0 z-10 mix-blend-multiply bg-gradient-to-t from-ink/90 via-ink/10 to-transparent" />

          <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex items-end justify-between gap-4 p-6">
            <div key={activeItem.id} className="flex animate-captionIn flex-col gap-2">
              <h3 className="font-sans text-xl font-semibold uppercase tracking-tight leading-tight text-white">
                {activeItem.title}
              </h3>
              <span className="flex items-center gap-1.5 text-xs uppercase tracking-wide2 text-white/60">
                <MapPin size={22} />
                {activeItem.location}
              </span>
            </div>
            <div className="pointer-events-auto hidden shrink-0 gap-2 sm:flex">
              <button
                type="button"
                aria-label={t("portfolioPrevAria")}
                onClick={() => goTo(-1)}
                className="flex h-11 w-11 items-center justify-center bg-white/10 text-white backdrop-blur-sm transition-colors duration-200 ease-engineered hover:bg-white/20"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                type="button"
                aria-label={t("portfolioNextAria")}
                onClick={() => goTo(1)}
                className="flex h-11 w-11 items-center justify-center bg-white/10 text-white backdrop-blur-sm transition-colors duration-200 ease-engineered hover:bg-white/20"
              >
                <ChevronRight size={24} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
